<template>
  <div v-if="studentSieId">
    <page-header
      :actions="headerActionConfig"
      :page="{
        icon: 'fas fa-money-check-edit-alt',
        title: 'Historial de Reembolso',
        routes: ['Inicio', 'Historial de Reembolso'],
      }"
    />

    <b-row v-if="showAddRequest" class="mb-4">
      <b-col cols="12">
        <b-card class="bg-gray border-0">
          <b-row>
            <b-col cols="4">
              <custom-drop-down
                v-model="selectedAcademicYear"
                :clearable="false"
                fieldkey="id"
                fieldname="name"
                label="Año Académico:"
                :options="this.filterOptions.tapRequestAcademicYears"
                placeholder="Seleccione"
                @input="checkRequestDisbursements"
              />
            </b-col>
          </b-row>
          <b-row v-if="selectedAcademicYear && ssnOpen">
            <b-col cols="12">
              <request-reimbursement
                :academic-year-id="selectedAcademicYear.id"
                :tap-request-id="tapRequestId"
                @on-cancel="toggleAdd"
                @on-continue="onContinue"
              />
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <filter-manager
      ref="filterComponent"
      v-model="filters"
      class="mb-4"
      :display-advanced="false"
      :filters="filtersConfig"
      @search="onSearchHandler"
    />

    <status-leyend :status-list="statusLegend" />

    <custom-separator class="my-4" text="Resultado" />
    <custom-result-not-found v-if="results.length === 0" />
    <result-row
      v-for="(row, idx) of results"
      :key="`result-${idx}`"
      class="mb-3"
      :data="row"
    />
    <pagination
      v-if="pagination.totalItemCount > pagination.pageSize"
      :data="pagination"
      show-text
      @pagination-go-page="goToPage"
    />
  </div>
</template>

<script>
import Pagination from 'pagination';
import FilterManager from 'FilterManager';
import CustomDropDown from 'custom-drop-down';
import PageHeader from 'PageHeader';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import ResultRow from './components/ResultRow.vue';
import StatusLeyend from '@/views/students/history-location/components/HistoryLocationStatusLeyend.vue';
import RequestReimbursement from '@/views/students/history-location/components/RequestReimbursement.vue';
import { getStatusLegend as _getStatusLegend } from '@/services/api/historyLocation.api';
import {
  getDisbursementRequestHistory,
  getInitFilters,
  checkTapRequestDisbursementByStudent,
} from '@/services/api/disbursement.api.js';
import alertMixin from 'utils/mixins/alert.mixin';

export default {
  name: 'RefundHistory',
  props: { studentSieId: { type: [String, Number], default: null } },
  mixins: [alertMixin],
  components: {
    FilterManager,
    Pagination,
    CustomSeparator,
    CustomResultNotFound,
    ResultRow,
    PageHeader,
    StatusLeyend,
    CustomDropDown,
    RequestReimbursement,
  },
  data: () => ({
    pagination: {
      currentPage: 1,
      totalItemCount: 0,
      pageCount: 1,
      count: 0,
      pageSize: 5,
    },
    filters: {},
    filterOptions: {
      academicYears: [],
      tapRequestAcademicYears: [],
      statuses: [],
    },
    statusLegend: [],
    results: [],
    selectedAcademicYear: null,
    tapRequestId: null,
    showAddRequest: false,
    ssnOpen: false,
  }),
  computed: {
    filtersConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filterOptions.academicYears,
          placeholder: 'Año Académico',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-12 col-md-4',
          initialValue: this.filters.academicYear,
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.filterOptions.statuses,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-12 col-md-4',
          initialValue: this.filters.status,
        },
        {
          name: 'number',
          component: 'CustomInput',
          class: 'col-12 col-md-4',
          placeholder: 'Escriba Número de Reembolso',
          clearable: true,
          icon: 'fas fa-search color-primary-2',
          leftIcon: true,
        },
      ];
    },
    headerActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left fa-fw',
          variant: 'outline-success',
          click: () => this.$router.push({ name: 'home' }),
        },
        {
          text: 'Solicitar Reembolso',
          icon: 'fas fa-circle-dollar-to-slot',
          variant: 'success',

          click: () => this.toggleAdd(),
        },
      ];
    },
  },
  async created() {
    await Promise.all([
      this.getInitFilters(),
      this.onSearchHandler(),
      this.getStatusLegend(),
    ]);
  },
  methods: {
    async getInitFilters() {
      const { data } = await getInitFilters(Number(this.studentSieId));

      this.filterOptions = {
        ...this.filterOptions,
        statuses: data.statuses,
        academicYears: data.academicYears,
        tapRequestAcademicYears: data.tapRequestAcademicYears,
      };
    },
    async getStatusLegend() {
      const { data } = await _getStatusLegend();

      this.statusLegend = data;
    },
    openCloseResultHandler(value, idx) {
      this.results = this.results.map((r, i) => ({
        ...r,
        isOpen: i === idx ? !value : false,
      }));
    },
    async goToPage(page) {
      await this.onSearchHandler(page);
    },
    async onSearchHandler(page = 1) {
      this.pagination.currentPage = page;

      const model = { ...this.filters };

      const params = {
        studentSieId: Number(this.studentSieId),
        AcademicYearId: model.academicYear?.id,
        statusId: model.status?.id,
        number: model.number,
        pageIndex: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
      };

      const { data } = await getDisbursementRequestHistory(params);
      this.results = [...data.data];
      this.pagination = {
        ...this.pagination,
        totalItemCount: data.totalItemCount,
        pageCount: data.pageCount,
        currentPage: data.currentPage,
        count: data.count,
      };
    },
    onContinue(result) {
      if (result.ssnValid) {
        this.$router.push({
          name: 'student-reinbursement',
          params: {
            tapRequestId: this.tapRequestId,
            requestId: 0,
            ssn: result.ssn,
            fromRefundHistory: true,
          },
        });
      } else {
        this.ShowToast(
          'Alerta',
          'El número de seguro social no se encontró en en el Sistema de Información Financiera del Departamento de Educación (SIFDE).',
          'error'
        );
      }
    },
    toggleAdd() {
      this.showAddRequest = !this.showAddRequest;
      this.ssnOpen = false;
      this.tapRequestId = null;
      this.selectedAcademicYear = null;
    },
    async checkRequestDisbursements() {
      await checkTapRequestDisbursementByStudent(
        Number(this.studentSieId),
        this.selectedAcademicYear.id
      )
        .then(({ data }) => {
          if (data && data.existAnyDisbursement) {
            this.$router.push({
              name: 'student-reinbursement',
              params: {
                tapRequestId: data.tapRequestId,
                requestId: data.disbursementRequestInNewId,
                fromRefundHistory: true,
              },
            });
          } else {
            // Request SSN
            this.tapRequestId = data.tapRequestId;
            this.ssnOpen = true;
          }
        })
        .catch((err) =>
          this.ShowToast(
            'Alerta',
            err.response.data.message || err.message,
            'error'
          )
        );
    },
  },
};
</script>
<style scoped>
::v-deep .orange-icon {
  font-size: 22px !important;
}
</style>
